import React, { useEffect, useContext, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DocumentLink } from "../molecules/DocumentLink";
import trackService, { AUDITEVENTTYPE } from "services/trackService";
import { PDFViewerContext } from "contexts/PdfViewerContext";
import SearchBar from "components/molecules/SearchBar";
import { usePlanDocumentsSearchFilter } from "./usePlanDocumentsSearchFilter";
import { DocumentHeader } from "types/documents/DocumentHeader";
import { sendCustomEvent } from "common/utility";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { notifications$ } from "services/subjects";
import { useQuery } from "@tanstack/react-query";
import documentService from "services/documentService";
import { filter, firstValueFrom, map } from "rxjs";
import {
  Box,
  Container,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useNotifyError } from "hooks/useNotifyError";

const SERVICE_URL = "api/members/documents";

export const PlanDocuments = (): JSX.Element => {
  const { addAndOpenDocument } = useContext(PDFViewerContext);
  const {
    data: documents,
    isLoading: isLoadingDocuments,
    error: documentsError,
  } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => {
      const observable$ = documentService.get$.pipe(
        filter((response) => !!response?.response),
        map((response) => response.response.results)
      );

      return await firstValueFrom(observable$);
    },
  });
  useNotifyError(documentsError, "Error loading documents");
  const {
    data: educationalResources,
    isLoading: isLoadingEducationalResources,
    error: educationalResourcesError,
  } = useQuery({
    queryKey: ["educationalResources"],
    queryFn: async () => {
      const observable$ = documentService.getEducationalResources$().pipe(
        filter((response) => !!response?.response),
        map((response) => response.response)
      );

      return (await firstValueFrom(observable$)) as DocumentHeader[];
    },
  });
  useNotifyError(
    educationalResourcesError,
    "Error loading educational resources"
  );
  const {
    data: additionalResources,
    isLoading: isLoadingAdditionalResources,
    error: additionalResourcesError,
  } = useQuery({
    queryKey: ["additionalResources"],
    queryFn: async () => {
      const observable$ = documentService.getAdditionalResources$();

      return await firstValueFrom(observable$);
    },
  });
  useNotifyError(additionalResourcesError, "Error loading additional resources");
  const {
    filteredDocuments,
    filteredEducationalResources,
    filteredAdditionalResources,
    setSearchTerm,
  } = usePlanDocumentsSearchFilter(
    documents,
    educationalResources,
    additionalResources
  );

  useEffect(() => {
    const trackPageVisitSub = trackService
      .customEvent$({ eventType: AUDITEVENTTYPE.MemberPortalViewPlanDocuments })
      .subscribe();

    return () => {
      trackPageVisitSub.unsubscribe();
    };
  }, []);

  const handleDocumentClick = useCallback(
    (document: DocumentHeader) => {
      const field = document.download_custom_event_name;
      const customEventType =
        AUDITEVENTTYPE[field as keyof typeof AUDITEVENTTYPE];
      customEventType && sendCustomEvent({ eventType: customEventType });
      addAndOpenDocument({
        serviceUrl: SERVICE_URL,
        documentPath: document.id,
        displayName: document.filename || "",
      });
    },
    [addAndOpenDocument]
  );

  return (
    <Container>
      <Stack mb={6}>
        <SearchBar onChange={setSearchTerm} placeholder="Search document..." />
      </Stack>
      <Typography variant="h2" className="mb-3" fontWeight="bold" mb={2}>
        Plan Documents
      </Typography>
      <Stack
        component="ul"
        p={0}
        m={0}
        spacing={0.5}
        mb={8}
        data-testid="documents"
      >
        {isLoadingDocuments ? (
          <Stack spacing={1} data-testid="loading-documents">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton variant="rounded" width="100%" height={20} key={i} />
            ))}
          </Stack>
        ) : (
          filteredDocuments
            ?.filter(
              (document) =>
                document.data.document_type?.toLocaleLowerCase() !== "eob"
            )
            .map((document, i) => (
              <DocumentLink
                key={i}
                document={document}
                onDocumentClick={handleDocumentClick}
              />
            ))
        )}
      </Stack>
      <Typography variant="h2" className="mb-3" fontWeight="bold" mb={2}>
        Educational Resources
      </Typography>
      <Stack
        component="ul"
        p={0}
        m={0}
        spacing={0.5}
        mb={8}
        data-testid="educational-resources"
      >
        {isLoadingEducationalResources ? (
          <Stack spacing={1} data-testid="loading-educational-resources">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton variant="rounded" width="100%" height={20} key={i} />
            ))}
          </Stack>
        ) : (
          filteredEducationalResources?.map((resource, i) => (
            <DocumentLink
              key={i}
              document={resource}
              onDocumentClick={handleDocumentClick}
            />
          ))
        )}
      </Stack>
      <Typography variant="h2" className="mb-3" fontWeight="bold" mb={2}>
        Additional Resources
      </Typography>
      <Stack
        component="ul"
        p={0}
        m={0}
        spacing={0.5}
        mb={8}
        data-testid="additional-resources"
      >
        {isLoadingAdditionalResources ? (
          <Stack spacing={1} data-testid="loading-additional-resources">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton variant="rounded" width="100%" height={20} key={i} />
            ))}
          </Stack>
        ) : (
          (filteredAdditionalResources || []).map((resource, i) =>
            "href" in resource ? (
              <Box
                key={resource.filename}
                component="li"
                sx={{ listStyle: "none" }}
              >
                <Link
                  href="https://alignment.access.mcg.com/index"
                  target="_alignment_mcg"
                  sx={{
                    textDecoration: "none",
                    display: "flex",
                    columnGap: 2,
                    alignItems: "center",
                  }}
                  onClick={() => {
                    const customEvent =
                      AUDITEVENTTYPE[
                        resource.customEvent as keyof typeof AUDITEVENTTYPE
                      ];
                    if (!customEvent) return;
                    sendCustomEvent({
                      eventType: customEvent,
                    });
                  }}
                >
                  <FontAwesomeIcon
                    icon={faExternalLinkAlt}
                    aria-label="External Link"
                    size="sm"
                  />
                  <Box component="span">{resource.filename}</Box>
                </Link>
              </Box>
            ) : (
              <DocumentLink
                key={i}
                document={resource}
                onDocumentClick={handleDocumentClick}
              />
            )
          )
        )}
      </Stack>
      <Typography variant="body1" mb={3}>
        For additional member forms and resources, visit our{" "}
        <Link href="https://www.alignmenthealthplan.com/members/member-forms-resources">
          Members &amp; Resources Page
        </Link>
      </Typography>
    </Container>
  );
};

export default PlanDocuments;
